import { useHandler } from "@redotech/react-util/hook";
import { RedoOutboundLabelsRpcClient } from "@redotech/redo-merchant-fulfillment-rpc/client";
import { FulfillmentGroupData } from "@redotech/redo-model/fulfillments/fulfillment-group";
import { FulfillmentOrderAddress } from "@redotech/redo-model/fulfillments/fulfillment-order-address";
import { toast } from "@redotech/redo-web/alert";
import { useState } from "react";

interface UseConfirmEditAddressProps {
  fulfillmentGroups: readonly FulfillmentGroupData[];
  client: RedoOutboundLabelsRpcClient;
  triggerRefresh: () => void;
}

export const useConfirmEditAddress = ({
  fulfillmentGroups,
  client,
  triggerRefresh,
}: UseConfirmEditAddressProps) => {
  const [confirmEditAddressModalAddress, setConfirmEditAddressModalAddress] =
    useState<FulfillmentOrderAddress | null>(null);

  const onEditAddress = useHandler(async (address: FulfillmentOrderAddress) => {
    if (fulfillmentGroups.length !== 1) {
      return;
    }
    try {
      const result = await client.updateShippingAddress({
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        fulfillmentGroupId: fulfillmentGroups[0]._id,
        address,
      });
      // If we failed to update the marketplace, and didn't update the address in Redo, show modal to check with the user if they are ok updating the address in Redo only
      if (
        !result.salesChannelUpdateSuccess &&
        !result.redoAddressUpdateSuccess
      ) {
        setConfirmEditAddressModalAddress(address);
      }
    } catch (e) {
      toast("Error updating address", { variant: "error" });
    }
  });

  const onConfirmEditAddress = useHandler(async () => {
    if (confirmEditAddressModalAddress === null) {
      return;
    }
    try {
      await client.updateShippingAddress({
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        fulfillmentGroupId: fulfillmentGroups[0]._id,
        address: confirmEditAddressModalAddress,
        updateRedoOnMarketplaceFailure: true,
      });
    } catch (e) {
      toast("Error updating address", { variant: "error" });
    } finally {
      setConfirmEditAddressModalAddress(null);
    }
  });

  const onUseAutoCleanedAddress = useHandler(
    async (useAutoCleanedAddress: boolean) => {
      if (fulfillmentGroups.length !== 1) {
        return;
      }
      try {
        await client.setUseOriginalAddress({
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          fulfillmentGroupId: fulfillmentGroups[0]._id,
          useOriginalAddress: !useAutoCleanedAddress,
        });
      } catch (e) {
        toast("Error updating address preference", { variant: "error" });
      }
    },
  );

  const closeConfirmModal = useHandler(() => {
    setConfirmEditAddressModalAddress(null);
  });

  return {
    confirmEditAddressModalAddress,
    onEditAddress,
    onConfirmEditAddress,
    onUseAutoCleanedAddress,
    closeConfirmModal,
  };
};
